<template>
  <div class="progress-flashcard-component-container">
    <page-header :breadcrumbs="breadcrumbs">
      <template v-slot:topbar>
        <h1>{{ title }}</h1>
        <improve-question :question="currentFlashcard" />
      </template>
    </page-header>
    <flashcard
      v-if="currentFlashcard.id && currentFlashcardState"
      :answered="true"
      :answer-ids="answerIds"
      :correct="currentFlashcardState.correct"
      :flashcard="currentFlashcard"
      :category="currentFlashcardCategory"
      :flashcard-state="currentFlashcardState"
      :raw-answer-data="rawAnswerData"
      @set-answer-ids="setAnswerIds"
      @set-raw-answer-data="setRawAnswerData"
    />
    <page-footer>
      <router-link
        v-if="previousFlashcardId"
        :to="previousRoute"
        class="previous"
      >
        <icon
          name="arrow"
          variant="a8aab7"
        />
        Previous
      </router-link>
      <router-link
        v-if="nextFlashcardId"
        :to="nextRoute"
        class="next"
      >
        Next
        <icon
          name="arrow"
          variant="a8aab7"
        />
      </router-link>
      <div class="progress">
        <div class="counter">
          {{ currentStateIndex + 1 }}/{{ states.length }}
        </div>
        <pure-progress-bar
          :answered="currentStateIndex + 1"
          :total="states.length"
        />
      </div>
      <router-link
        :to="backRoute"
        class="back"
      >
        <icon
          class="list-icon"
          name="hamburger"
          variant="a8aab7"
        />Back To List
      </router-link>
    </page-footer>
  </div>
</template>

<script>
import Flashcard from '@components/flashcards/flashcard';
import Icon from '@components/singletons/icon';
import ImproveQuestion from '@components/singletons/improveQuestion';
import PageHeader from '@components/singletons/pageHeader';
import PageFooter from '@components/singletons/pageFooter';
import PureProgressBar from '@components/progress/PureProgressBar';

import FlashcardViewMixin from '@/mixins/flashcardViews';
import GlobalMixin from '@/mixins/global';
import QuizMixin from '@/mixins/quizzes';
import WuxSimulationSorting from '@/mixins/wux-simulation-sorting';
import BlueprintMixin from '@/mixins/blueprint';

export default {
  components: {
    Flashcard, ImproveQuestion, PageFooter, PageHeader, PureProgressBar, Icon,
  },
  mixins: [FlashcardViewMixin, GlobalMixin, QuizMixin, WuxSimulationSorting, BlueprintMixin],
  computed: {
    backRoute() {
      if (this.isBlueprint) {
        return {
          name: 'sims#results',
          params: { simId: this.currentFlashcardCategory.id, blueprintId: this.blueprintId },
          query: this.currentQuery,
        };
      }

      if (this.isSimulation) {
        return {
          name: 'sims#results',
          params: { categoryId: this.currentFlashcardCategory.id },
          query: this.currentQuery,
          meta: { simulation: true },
        };
      }

      if (this.currentResource.destination === 'test_simulations') {
        return {
          name: 'testSimulations#results#questions',
          params: { categoryId: this.currentFlashcardCategory.id },
          query: this.currentQuery,
        };
      } else {
        return { name: `${this.routePrefix}#results`, params: { categoryId: this.currentFlashcardCategory.id }, query: this.currentQuery };
      }
    },
    breadcrumbs() {
      if (this.isBlueprint) {
        return [
          {
            name: this.currentFlashcardCategory.name,
            route: {
              name: 'sims#performance',
              params: { simId: this.currentFlashcardCategory.id, blueprintId: this.blueprintId}, query: this.currentQuery},
          },
          {
            name: this.blueprintGrouping.name,
            route: {
              name: 'sims#results',
              params: { categoryId: this.currentFlashcardCategory.id, blueprintId: this.blueprintId}, query: this.currentQuery,
            },
          },
          {
            name: 'Review',
            route: {},
          },
        ];
      }
      if (this.currentResource.destination === 'test_simulations') {
        if (!this.currentFlashcardCategory) return [];
        let result = { name: this.currentFlashcardCategory.name, route: { name: 'testSimulations#results', params: {categoryId: this.currentFlashcardCategory.id}, query: this.currentQuery}};
        let list = { name: 'Questions', route: { name: 'testSimulations#results#questions', params: {categoryId: this.currentFlashcardCategory.id}, query: this.currentQuery}};
        let current = { name: 'Question', route: {}};
        return [result, list, current];
      } else if (this.currentFlashcardCategory) {
        let categories = this.currentFlashcardCategory.path.split('.').map(id => this.$store.getters['categories/categories'][id]);
        let crumbs = categories.filter(c => c.id !== this.currentFlashcardCategory.id).map(c => {
          return { name: c.name, route: {name: `${this.routePrefix}#index`, params: { categoryId: c.id}, query: this.currentQuery}};
        });
        crumbs.push({ name: this.currentFlashcardCategory.name, route: {name: `${this.routePrefix}#results`, params: { categoryId: this.currentFlashcardCategory.id}, query: this.currentQuery} });
        crumbs.push({name: 'Review', route: {}});
        return crumbs;
      } else {
        return [];
      }
    },
    currentFlashcardCategory() {
      return this.$store.getters['categories/categories'][this.$route.params.categoryId];
    },
    currentFlashcardState() {
      const key = `${this.$route.params.flashcardId}-category-${this.currentFlashcardCategory.id}`;
      return this.$store.getters['flashcardStates/flashcardStates'][key] || (
        this.isSimulation ? {} : null
      );
    },
    currentStateIndex() {
      if (this.currentFlashcardState) {
        return this.states.findIndex(s => s.flashcardId == this.currentFlashcardState.flashcardId);
      }
    },
    flashcardRouteName() {
      if (this.isSimulation) {
        return 'sims#results#question';
      }
      if (this.currentResource.destination === 'test_simulations') {
        return 'testSimulations#results#question';
      } else {
        return `${this.routePrefix}#results#question`;
      }
    },
    isSimulation() {
      return this.$route.name === 'sims#results#question';
    },
    nextFlashcardId() {
      const index = this.currentStateIndex + 1;
      if (Number.isInteger(index) && index < this.states.length) {
        return this.states[index].flashcardId;
      }
    },
    nextRoute() {
      return {
        name: this.flashcardRouteName,
        params: { categoryId: this.currentFlashcardCategory.id, flashcardId: this.nextFlashcardId },
        query: this.currentQuery,
        meta: { simulation: this.isSimulation },
      };
    },
    previousFlashcardId() {
      const index = this.currentStateIndex - 1;
      if (Number.isInteger(index) && index >= 0) {
        return this.states[index].flashcardId;
      }
    },
    previousRoute() {
      return {
        name: this.flashcardRouteName,
        params: { categoryId: this.currentFlashcardCategory.id, flashcardId: this.previousFlashcardId },
        query: this.currentQuery,
        meta: { simulation: this.isSimulation },
      };
    },
    states() {
      if (this.isBlueprint) {
        return this.getBlueprintFlashcardStates(this.currentFlashcardCategory.id);
      }

      const states = this.$store.getters['flashcardStates/flashcardStateList'].filter(fs => fs.categoryId == this.currentFlashcardCategory.id);

      if (this.isSimulation) {
        return [...states].sort(this.flashcardStateComparator);
      }
      return [...states].sort((a, b) => b.mobileCreatedAt - a.mobileCreatedAt);
    },
    title() {
      if (this.currentFlashcardCategory) {
        return this.currentFlashcardCategory.name;
      }
    },
  },
  created() {
    if (!this.currentFlashcardState) {
      this.$store.dispatch('flashcardStates/fetchStates', ['flashcards', 'stats']).then(() => {
        if (!this.currentFlashcardState && !this.isSimulation) {
          this.$router.go(-1);
        }
       });
    }
    if (!this.currentFlashcard.id) {
      this.$store.dispatch('flashcards/fetchFlashcards', {
        categoryId: this.currentFlashcardCategory.id,
        rateLimited: this.rateLimitingEnabled,
      });
    }
  },
};
</script>

<style lang='scss' scoped>
  .progress-flashcard-component-container {
    @include flashcard-view;

    .page-footer-component-container {
      .progress {
        align-items: center;
        display: flex;
        flex: 1;

        .counter {
          color: #354052;
          font-size: rem-calc(12);
          font-weight: $semi-bold;
          margin-right: rem-calc(8);
        }
      }

      > a {
        @include hlt-btn-1;
        font-size: rem-calc(14);
        padding: rem-calc(6) rem-calc(12);

        &.back {
          margin-left: auto;

          @include breakpoint(small) {
            display: none;
          }
        }

        &.next {
          margin-right: rem-calc(12);

          img {
            margin: 0 0 0 rem-calc(7);
          }
        }

        &.previous {
          margin-right: rem-calc(12);

          img {
            transform: rotate(180deg);
          }
        }

        img {
          height: rem-calc(16);
          margin: 0 rem-calc(7) 0 0;
          width: rem-calc(16);
        }

        .list-icon {
          height: auto;
          margin: 0 rem-calc(7) 0 0;
          width: rem-calc(16);
        }
      }
    }
  }
</style>
